import React from 'react';
import styled, { keyframes } from 'styled-components/macro';
import useMedia from 'hooks/useMedia';
import { mix } from 'polished';
import { string, shape, bool } from 'prop-types';
import Cookies from 'js-cookie';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

import { LogoWrapperBase as LogoWrapper } from 'components/Header/HeaderPanel/HeaderPanel';
import TopBar from 'components/Header/TopBar/TopBar';
import Anchor from 'components/base/Anchor/Anchor';

import createAuthPath from 'helpers/createAuthPath';
import modularScale from 'styles/helpers/modular-scale';

import { ReactComponent as OpusLogo } from 'icons/icon-opus-logo.svg';
import { ReactComponent as Circles } from 'icons/login-circles.svg';
import Footer from 'components/Footer/Footer';

const wave = keyframes`
	from, to {
    transform: scale(1);
	}

	50%{
    transform: scale(1.1);
  }
`;

const StyledCircles = styled(Circles)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
  width: 150vmax;
  height: 150vmax;

  circle {
    will-change: transform;
    transform-origin: 50% 50%;
    animation: ${wave} 5s cubic-bezier(0.5, 0, 0.5, 1) 999;

    &:nth-child(2) {
      animation-delay: 0.3s;
    }
    &:nth-child(3) {
      animation-delay: 0.6s;
    }
    &:nth-child(4) {
      animation-delay: 0.9s;
    }
    &:nth-child(5) {
      animation-delay: 1.2s;
    }
  }

  @media screen and (min-width: 35em) {
    width: 120vmax;
    height: 120vmax;
  }
`;

const LoginContent = styled('div')`
  position: relative;
  flex: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  color: ${({ theme }) => theme.DeepBlue};
  overflow: hidden;
  background: linear-gradient(
    90deg,
    ${({ theme }) => mix(0.75, theme.GradientBlue, theme.NightBlue)},
    ${({ theme }) => mix(0.75, theme.GradientGreen, theme.NightBlue)}
  );
`;

const LogoText = styled('p')`
  font-family: OpenSans, sans-serif;
  font-size: ${({ isMobileView }) =>
    isMobileView ? '16px' : '20px'} !important;
  letter-spacing: 0.05px;
  text-align: center;
  color: #ffffff;
  margin: 0.8em auto -0.2em auto !important;
`;

const MainContentWrapper = styled('div')`
  margin: auto;
  padding-bottom: ${({ isMobileView }) => (isMobileView ? '5em' : '7.5em')};
`;

const LoginWrapper = styled('div')`
  background: none;
  max-width: 25em;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  position: relative;
  width: 100%;
`;

const LoginButton = styled('a')`
  display: inline-block;
  width: 183px;
  height: 40px;
  text-align: center;
  background: #ffffff;
  color: #0bd6a7;
  letter-spacing: 0.04px;
  line-height: 27px;
  padding: ${modularScale(-5)} ${modularScale(3)};
  font-weight: 600;
  font-family: OpenSans, sans-serif;
  border-radius: 3px;
  transition: ${({ theme }) =>
    `background ${theme.transitionDuration} ${theme.timingFunction}`};

  &:hover {
    color: white;
    background: #0bd6a7;
  }
`;

const LoginProblems = styled('p')`
  color: #fff;
  font-size: 13px;
  letter-spacing: 0.04px;
  margin-bottom: 0;
  margin-top: 2.5em;
`;

const authPath = createAuthPath();

const Login = ({ location: { from }, isAuthenticated }) => {
  Cookies.set('from', from ? from.pathname + from.search : '/');
  const isMobileView = useMedia('(max-width: 45em)');

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <LoginContent>
      <StyledCircles />
      <TopBar />
      <MainContentWrapper isMobileView={isMobileView}>
        <LogoWrapper
          logoWidth={isMobileView ? '13.5em' : '28.5em'}
          isMobileView={isMobileView}
        >
          <OpusLogo />
          <LogoText isMobileView={isMobileView}>
            Connecting the body of work
          </LogoText>
        </LogoWrapper>
        <LoginWrapper data-pw="login-wrapper">
          <LoginButton href={`${authPath}/login/okta`}>Login</LoginButton>
          <LoginProblems>
            Need help signing in?&nbsp;&nbsp;&nbsp;
            <Anchor to="mailto:opus-support@wmg.com">Contact us</Anchor>
          </LoginProblems>
        </LoginWrapper>
      </MainContentWrapper>
      <Footer />
    </LoginContent>
  );
};

Login.propTypes = {
  /** Indicates if the user is logged in. Used to disable links in the footer when logged out. */
  isAuthenticated: bool.isRequired,
  /** The location that redirected to the Login page. After login we want to show this page again. */
  location: shape({
    from: shape({
      pathName: string,
    }),
  }),
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.authentication.isAuthenticated,
});
export default connect(mapStateToProps)(Login);
